/**
 * MobileAuditTemplatesList — template catalogue cards ( <lg ). Batch 6.3.
 * GET /audits-qa/templates — same fields desktop uses; CQC key questions
 * render as pills verbatim from the payload. Manager CRUD flows through
 * the same POST/PATCH/DELETE /audits-qa/templates + POST /duplicate
 * endpoints via an inline dialog. Care workers never see the kebab.
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Copy, Pencil, Ban, LayoutTemplate, MoreVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export default function MobileAuditTemplatesList({ canManage, refreshTick, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [constants, setConstants] = useState({ categories: [], cqc_key_questions: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audits-qa/templates", { params: { active_only: !showInactive } });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => { api.get("/audits-qa/constants").then((r) => setConstants(r.data)).catch(() => {}); }, []);

  const dup = async (row) => {
    try { await api.post(`/audits-qa/templates/${row.id}/duplicate`); toast.success("Template duplicated"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deact = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"?`)) return;
    try { await api.delete(`/audits-qa/templates/${row.id}`); toast.success("Template deactivated"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const catLabel = (key) => (constants.categories || []).find((c) => c.key === key)?.label || key;

  return (
    <div className="space-y-3" data-testid="mobile-audits-templates">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-slate-500 tabular-nums" data-testid="mobile-templates-count">
          {rows.length} template{rows.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <label className="text-xs text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="mobile-templates-show-inactive" />
              Show deactivated
            </label>
          )}
          {canManage && (
            <Button size="sm" onClick={() => setCreating(true)} data-testid="btn-mobile-template-new"
              className="h-11 min-h-[44px] bg-sky-700 hover:bg-sky-800 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> New
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 p-4">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-templates-empty">
          <LayoutTemplate className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-sm font-semibold text-slate-700">No templates.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mobile-templates-list">
          {rows.map((t) => (
            <li key={t.id} data-testid={`mobile-template-row-${t.id}`} className={`rounded-lg border border-slate-200 bg-white p-3 ${!t.is_active ? "opacity-70" : ""}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`mobile-template-name-${t.id}`}>
                    {t.name}
                  </div>
                  {t.description && (
                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
                  )}
                </div>
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`btn-mobile-template-kebab-${t.id}`}
                        className="min-h-[44px] w-11 h-11 -mt-1 -mr-1 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                        aria-label="Template actions"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => setEditing(t)} data-testid={`btn-mobile-template-edit-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => dup(t)} data-testid={`btn-mobile-template-duplicate-${t.id}`}>
                        <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
                      </DropdownMenuItem>
                      {t.is_active && (
                        <DropdownMenuItem onClick={() => deact(t)} data-testid={`btn-mobile-template-deactivate-${t.id}`} className="text-red-600 focus:text-red-700">
                          <Ban className="w-3.5 h-3.5 mr-2" /> Deactivate
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Category */}
              <div className="text-[11px] text-slate-600 mt-1">
                <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Category</span>
                <span data-testid={`mobile-template-category-${t.id}`}>{catLabel(t.category)}</span>
              </div>

              {/* CQC pills (verbatim from server) */}
              {(t.cqc_key_questions || []).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`mobile-template-cqc-${t.id}`}>
                  {t.cqc_key_questions.map((k) => (
                    <span key={k} className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200">
                      {k}
                    </span>
                  ))}
                </div>
              )}

              {/* Active/Deactivated chip */}
              <div className="mt-1.5">
                {t.is_active ? (
                  <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300" data-testid={`mobile-template-active-${t.id}`}>
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-300" data-testid={`mobile-template-inactive-${t.id}`}>
                    Deactivated
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <TemplateDialog
        open={creating || !!editing} template={editing} constants={constants}
        onOpenChange={(v) => { if (!v) { setEditing(null); setCreating(false); } }}
        onSaved={() => { setEditing(null); setCreating(false); load(); onChanged?.(); }}
      />
    </div>
  );
}

/** Same fields + same endpoints as desktop TemplateDialog. */
function TemplateDialog({ open, template, constants, onOpenChange, onSaved }) {
  const [form, setForm] = useState(defaults());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...defaults(), ...(template || {}) }); }, [open, template]);
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleFive = (k) => patch("cqc_key_questions", (form.cqc_key_questions || []).includes(k) ? form.cqc_key_questions.filter((x) => x !== k) : [...(form.cqc_key_questions || []), k]);
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      if (template?.id) await api.patch(`/audits-qa/templates/${template.id}`, form);
      else await api.post("/audits-qa/templates", form);
      toast.success(template?.id ? "Template updated" : "Template created");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="mobile-template-dialog">
        <DialogHeader><DialogTitle>{template?.id ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <FL label="Name"><Input value={form.name} onChange={(e) => patch("name", e.target.value)} data-testid="mobile-template-input-name" className="h-11" /></FL>
          </div>
          <FL label="Category">
            <select value={form.category} onChange={(e) => patch("category", e.target.value)} data-testid="mobile-template-input-category"
              className="min-h-[44px] h-11 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
              {(constants.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </FL>
          <div />
          <div className="md:col-span-2">
            <FL label="CQC Five Key Questions">
              <div className="flex flex-wrap gap-2 mt-1">
                {(constants.cqc_key_questions || []).map((k) => (
                  <label key={k} className="flex items-center gap-1 text-xs text-slate-700 min-h-[44px] pr-2">
                    <input type="checkbox" checked={(form.cqc_key_questions || []).includes(k)} onChange={() => toggleFive(k)} data-testid={`mobile-template-kq-${k}`} />
                    {k}
                  </label>
                ))}
              </div>
            </FL>
          </div>
          <div className="md:col-span-2">
            <FL label="Description"><Textarea rows={3} value={form.description || ""} onChange={(e) => patch("description", e.target.value)} /></FL>
          </div>
          <div className="md:col-span-2 flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => patch("is_active", e.target.checked)} />
            <span className="text-slate-700">Active</span>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11">Cancel</Button>
          <Button onClick={save} disabled={saving} className="h-11 bg-sky-700 hover:bg-sky-800" data-testid="btn-mobile-template-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaults() {
  return { name: "", category: "other", cqc_key_questions: [], cqc_quality_statements: [], description: "", is_active: true, checklist: [] };
}
function FL({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
